import React, { useEffect, useState, useCallback } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiUrl } from '../../utils/api';
import useInlineFillHeight from '../../hooks/useInlineFillHeight';
import './EditTestCycleModal.css';

/**
 * Modal de edição de Ciclo de Teste
 * Campos: Nome, Descrição, Pré-condição
 */
const DOMAIN_OS = {
  'Front-end': ['Windows', 'Linux', 'MacOS'],
  'Back-end': ['Windows', 'Linux', 'MacOS'],
  'Mobile': ['iOS', 'Android'],
  'Desktop': ['Windows', 'Linux', 'MacOS'],
};

const EditTestCycleModal = ({ isOpen, onClose, onSuccess, cycle, readOnly = false }) => {
  const { showError, showSuccess } = useNotification();
  const [inlineRef, inlineHeight] = useInlineFillHeight(isOpen);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    preconditions: '',
    jira_story_key: '',
    domain: '',
    operating_system: '',
    device_version: '',
    configuration: '',
    test_environment: '',
    strategy: ''
  });
  const [jiraBaseUrl, setJiraBaseUrl] = useState('');

  // Abas e dados auxiliares
  const [tab, setTab] = useState('cases'); // 'cases' | 'history'
  const [linkedCases, setLinkedCases] = useState([]);
  // Guarda estado inicial e alterações pendentes para salvar apenas ao clicar em "Salvar Ciclo"
  const [initialLinkedIds, setInitialLinkedIds] = useState([]);
  const [pendingAddIds, setPendingAddIds] = useState([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [availableCases, setAvailableCases] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [caseFolders, setCaseFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [selectedAvailableIds, setSelectedAvailableIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [linkedSearchTerm, setLinkedSearchTerm] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Tradução amigável para tipos de ação do histórico
  const actionTypeLabels = {
    created: 'Ciclo criado',
    updated: 'Atualização de campos',
    test_updated: 'Atualização de campos',
    responsible_changed: 'Responsável alterado',
    started: 'Execução iniciada',
    completed: 'Execução finalizada'
  };
  const getActionLabel = (type) => actionTypeLabels[type] || 'Alteração';

  const fetchJiraBaseUrl = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('integrations/jira-basic/config'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      const data = await res.json().catch(() => ({}));
      const url = data?.data?.url || '';
      setJiraBaseUrl(url || '');
    } catch (_) {
      setJiraBaseUrl('');
    }
  }, []);

  const loadLinkedCases = useCallback(async (id) => {
    if (!id) return;
    setCasesLoading(true);
    try {
      const url = getApiUrl(`test-repository/test-cycles/${id}/cases`);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setLinkedCases(list);
      setInitialLinkedIds(list.map(c => c.id));
      setPendingAddIds([]);
      setPendingRemoveIds([]);
    } catch (e) {
      console.error('Erro ao carregar casos vinculados:', e);
      showError('Falha ao carregar casos vinculados do ciclo');
    } finally {
      setCasesLoading(false);
    }
  }, [showError]);

  const loadCaseFolders = useCallback(async (projectId) => {
    if (!projectId) return;
    setFoldersLoading(true);
    try {
      const url = getApiUrl(`test-repository/folders?project_id=${projectId}&scope=cases`);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      if (!res.ok) {
        if (res.status !== 403) throw new Error(`HTTP ${res.status}`);
        setCaseFolders([]);
        return;
      }
      const json = await res.json().catch(() => ({}));
      const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      setCaseFolders(list);
    } catch (e) {
      console.error('Erro ao carregar pastas do repositório:', e);
      setCaseFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  const loadAvailableCases = useCallback(async (projectId, folderId = '') => {
    if (!projectId) return;
    setAvailableLoading(true);
    try {
      let url = 'test-repository/test-cases';
      const params = [`project_id=${projectId}`];
      if (folderId) params.push(`folder_id=${folderId}`);
      url += `?${params.join('&')}`;

      const res = await fetch(getApiUrl(url), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json().catch(() => ({}));
      const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setAvailableCases(list);
    } catch (e) {
      console.error('Erro ao carregar casos disponíveis:', e);
      setAvailableCases([]);
    } finally {
      setAvailableLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (id) => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      const url = getApiUrl(`executions/runs/${id}/history`);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      const json = await res.json().catch(() => ({}));
      const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      setHistory(list);
    } catch (e) {
      console.error('Erro ao carregar histórico do ciclo:', e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);



  useEffect(() => {
    if (isOpen && cycle) {
      setFormData({
        name: cycle.name || '',
        description: cycle.description || '',
        preconditions: cycle.preconditions || '',
        jira_story_key: cycle.jira_story_key || '',
        domain: cycle.domain || '',
        operating_system: cycle.operating_system || '',
        device_version: cycle.device_version || '',
        configuration: cycle.configuration || '',
        test_environment: cycle.test_environment || '',
        strategy: cycle.strategy || ''
      });
      setSearchTerm('');
      setLinkedSearchTerm('');
      setSelectedFolderId('');
      setSelectedAvailableIds(new Set());
      // Carregar dados complementares ao abrir
      loadLinkedCases(cycle.id);
      // Em modo somente leitura, não há necessidade de carregar casos disponíveis para vincular
      if (!readOnly) {
        loadCaseFolders(cycle.project_id);
        loadAvailableCases(cycle.project_id, '');
      }
      loadHistory(cycle.id);
      fetchJiraBaseUrl();
    }
  }, [isOpen, cycle, readOnly, loadLinkedCases, loadCaseFolders, loadAvailableCases, loadHistory, fetchJiraBaseUrl]);

  

  const tokenHeader = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
  });

  

  

  

  const handleClose = () => {
    if (loading) return;
    onClose && onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'domain' ? { operating_system: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (readOnly) {
      showError('Você não tem permissão para editar ciclos.');
      return;
    }
    if (!cycle?.id) {
      showError('Ciclo inválido.');
      return;
    }
    if (!formData.name?.trim()) {
      showError('Nome do ciclo é obrigatório');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const url = getApiUrl(`test-repository/test-cycles/${cycle.id}`);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          preconditions: formData.preconditions,
          jira_story_key: formData.jira_story_key,
          domain: formData.domain || null,
          operating_system: formData.operating_system || null,
          device_version: formData.device_version || null,
          configuration: formData.configuration || null,
          test_environment: formData.test_environment || null,
          strategy: formData.strategy || null
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = err?.error || err?.message || `Erro ao atualizar ciclo (HTTP ${response.status})`;
        showError(msg);
        return;
      }

      const data = await response.json().catch(() => ({}));
      const updated = data?.data || data || { ...cycle, ...formData };

      // Persistir alterações de vínculo apenas agora
      // Adições em lote (se suportado)
      if (pendingAddIds.length > 0) {
        const addUrl = getApiUrl(`test-repository/test-cycles/${cycle.id}/cases`);
        const addRes = await fetch(addUrl, {
          method: 'POST',
          headers: tokenHeader(),
          body: JSON.stringify({ case_ids: pendingAddIds })
        });
        if (!addRes.ok) {
          const err = await addRes.json().catch(() => ({}));
          throw new Error(err?.error || 'Erro ao vincular casos (salvar)');
        }
      }

      // Remoções individuais (API atual aceita DELETE por caso)
      for (const caseId of pendingRemoveIds) {
        const delUrl = getApiUrl(`test-repository/test-cycles/${cycle.id}/cases/${caseId}`);
        const delRes = await fetch(delUrl, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
        });
        if (!delRes.ok) {
          const err = await delRes.json().catch(() => ({}));
          throw new Error(err?.error || `Erro ao desvincular caso #${caseId} (salvar)`);
        }
      }

      showSuccess('Ciclo atualizado com sucesso!');
      onSuccess && onSuccess(updated);
      // Após salvar, recarrega dados e limpa staging
      await loadLinkedCases(cycle.id);
      setPendingAddIds([]);
      setPendingRemoveIds([]);
      // Recarregar histórico e focar na aba de histórico após atualizar
      await loadHistory(cycle.id);
      setTab('history');
      handleClose();
    } catch (error) {
      console.error('Erro ao atualizar ciclo:', error);
      showError(error?.message || 'Falha na requisição ao atualizar ciclo');
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailableSelection = (caseId) => {
    setSelectedAvailableIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const handleAddCasesBulk = (caseIds) => {
    if (readOnly) {
      showError('Sem permissão para vincular casos ao ciclo.');
      return;
    }
    const ids = Array.from(new Set((caseIds || []).filter(Boolean)));
    if (ids.length === 0) return;

    const availableById = new Map((availableCases || []).map(c => [c.id, c]));
    setLinkedCases(prev => {
      const existing = new Set((prev || []).map(c => c.id));
      const next = [...(prev || [])];
      for (const id of ids) {
        if (existing.has(id)) continue;
        next.push(availableById.get(id) || { id });
        existing.add(id);
      }
      return next;
    });

    setPendingRemoveIds(prev => (prev || []).filter(id => !ids.includes(id)));
    setPendingAddIds(prev => {
      const set = new Set(prev || []);
      for (const id of ids) {
        if (initialLinkedIds.includes(id)) continue;
        set.add(id);
      }
      return Array.from(set);
    });
  };

  const handleLinkSelected = () => {
    const ids = Array.from(selectedAvailableIds || []);
    handleAddCasesBulk(ids);
    setSelectedAvailableIds(new Set());
  };

  const handleToggleSelectVisible = (visibleIds) => {
    const ids = selectedFolderId
      ? availableUnlinked.map(c => c.id)
      : (Array.isArray(visibleIds) ? visibleIds : []);
    setSelectedAvailableIds(prev => {
      const next = new Set(prev || []);
      const allSelected = ids.length > 0 && ids.every(id => next.has(id));
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  const handleLinkAllFromFolder = () => {
    if (!selectedFolderId) {
      showError('Selecione uma pasta para vincular todos os casos.');
      return;
    }
    const linkedIdSet = new Set((linkedCases || []).map(c => c.id));
    const ids = (availableCases || []).map(c => c.id).filter(id => !linkedIdSet.has(id));
    handleAddCasesBulk(ids);
    setSelectedAvailableIds(new Set());
  };

  // Vincular localmente; só persistir no salvar
  const handleAddCase = (caseId) => {
    if (!caseId || !cycle?.id) return;
    if (readOnly) {
      showError('Sem permissão para vincular casos ao ciclo.');
      return;
    }
    // Se já estiver na lista vinculada local, ignorar
    if (linkedCases.some(c => c.id === caseId)) return;

    // Encontrar dados do caso
    const found = availableCases.find(c => c.id === caseId) || { id: caseId };
    setLinkedCases(prev => [...prev, found]);

    // Se era originalmente vinculado e estava marcado para remoção, desfaz a remoção
    if (initialLinkedIds.includes(caseId)) {
      setPendingRemoveIds(prev => prev.filter(id => id !== caseId));
    } else {
      // Caso novo: marcar para adicionar
      setPendingAddIds(prev => (prev.includes(caseId) ? prev : [...prev, caseId]));
    }
  };




  // Remover localmente; só persistir no salvar
  const handleRemoveCase = (caseId) => {
    if (!caseId || !cycle?.id) return;
    if (readOnly) {
      showError('Sem permissão para desvincular casos do ciclo.');
      return;
    }
    setLinkedCases(prev => prev.filter(c => c.id !== caseId));

    if (initialLinkedIds.includes(caseId)) {
      // Era originalmente vinculado: marcar para remover
      setPendingRemoveIds(prev => (prev.includes(caseId) ? prev : [...prev, caseId]));
    } else {
      // Era um vínculo novo ainda não salvo: desfazer adição pendente
      setPendingAddIds(prev => prev.filter(id => id !== caseId));
    }
  };

  if (!isOpen) return null;

  const flattenFolderOptions = (nodes, level = 0, acc = []) => {
    for (const node of nodes || []) {
      const indent = level > 0 ? `${'—'.repeat(level)} ` : '';
      acc.push({ id: String(node.id), label: `${indent}${node.name || `Pasta #${node.id}`}` });
      if (node.children && node.children.length) {
        flattenFolderOptions(node.children, level + 1, acc);
      }
    }
    return acc;
  };

  const availableUnlinked = (availableCases || [])
    .filter(c => !linkedCases.some(lc => lc.id === c.id));

  const availableFiltered = availableUnlinked
    .filter(c => (searchTerm ? (c.title || '').toLowerCase().includes(searchTerm.toLowerCase()) : true));

  const visibleAvailable = availableFiltered.slice(0, 50);
  const visibleAvailableIds = visibleAvailable.map(c => c.id);
  const selectableAvailableIds = selectedFolderId ? availableUnlinked.map(c => c.id) : visibleAvailableIds;
  const allSelectableSelected = selectableAvailableIds.length > 0 && selectableAvailableIds.every(id => selectedAvailableIds.has(id));
  const availableCountForSelection = selectedFolderId ? availableUnlinked.length : availableFiltered.length;
  const linkedSearch = linkedSearchTerm.trim().toLowerCase();
  const linkedFiltered = (linkedCases || []).filter(c => {
    if (!linkedSearch) return true;
    return `${c.title || ''} ${c.id || ''}`.toLowerCase().includes(linkedSearch);
  });
  const folderOptions = flattenFolderOptions(caseFolders || []);

  return (
    <div className="edit-cycle-modal edit-cycle-inline" ref={inlineRef} style={inlineHeight ? { height: inlineHeight } : undefined}>
      <div className="modal-header">
        <h2>{formData.name ? `(${formData.name})` : 'Editar Ciclo'}</h2>
      </div>

        <div className="edit-cycle-grid">
          {/* Coluna Esquerda: dados do ciclo e adicionar casos */}
          <div className="edit-cycle-left">
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nome do Ciclo de Teste</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Digite o nome do ciclo"
                    disabled={readOnly}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="description">Descrição</label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Descrição"
                    disabled={readOnly}
                  />
                </div>
              </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="preconditions">Pré-condição</label>
            <input
              type="text"
              id="preconditions"
              name="preconditions"
              value={formData.preconditions}
              onChange={handleInputChange}
              placeholder="Pré-condição"
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="jira_story_key">Jira Story Key</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                id="jira_story_key"
                name="jira_story_key"
                value={formData.jira_story_key}
                onChange={handleInputChange}
                placeholder="Insira o link da história"
                disabled={readOnly}
              />
              {formData.jira_story_key && (
                <button type="button" className="btn btn-link" onClick={() => {
                  const v = String(formData.jira_story_key || '').trim();
                  const isUrl = /^https?:\/\//i.test(v);
                  if (isUrl) {
                    window.open(v, '_blank');
                    return;
                  }
                  const base = jiraBaseUrl?.replace(/\/$/, '') || '';
                  const href = base ? `${base}/browse/${v}` : `https://jira.atlassian.com/browse/${v}`;
                  window.open(href, '_blank');
                }}>
                  Abrir História
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="form-section" style={{ marginTop: 8, marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Plataforma</div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Domínio</label>
              <select name="domain" value={formData.domain} onChange={handleInputChange} disabled={readOnly}>
                <option value="">Selecione</option>
                {Object.keys(DOMAIN_OS).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Sistema Operacional</label>
              <select name="operating_system" value={formData.operating_system} onChange={handleInputChange} disabled={readOnly || !formData.domain}>
                <option value="">Selecione</option>
                {(DOMAIN_OS[formData.domain] || []).map(os => <option key={os} value={os}>{os}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Device / Versão</label>
              <input type="text" name="device_version" value={formData.device_version} onChange={handleInputChange} disabled={readOnly} placeholder="Ex: iPhone 17 Pro, Windows 11..." />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Configuração</label>
              <select name="configuration" value={formData.configuration} onChange={handleInputChange} disabled={readOnly}>
                <option value="">Selecione</option>
                {['Web', 'Web Mobile', 'App', 'API'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Ambiente</label>
              <select name="test_environment" value={formData.test_environment} onChange={handleInputChange} disabled={readOnly}>
                <option value="">Selecione</option>
                {['Dev', 'Testes', 'Homolog', 'Pré-produção', 'Produção'].map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Estratégia</label>
              <select name="strategy" value={formData.strategy} onChange={handleInputChange} disabled={readOnly}>
                <option value="">Selecione</option>
                {['Funcional', 'Integração', 'Performance', 'Automatizado', 'Aceitação', 'API'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

              {/* Vincular Caso de Teste - transformar botão em título */}
              {!readOnly && (
                <div className="form-row">
                  <div className="form-group">
                    <div className="section-title">Vincular Caso de Teste</div>
                    <div className="cases-toolbar">
                      <select
                        value={selectedFolderId}
                        disabled={foldersLoading || availableLoading}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSelectedFolderId(next);
                          setSelectedAvailableIds(new Set());
                          loadAvailableCases(cycle?.project_id, next);
                        }}
                      >
                        <option value="">Todas as pastas</option>
                        {folderOptions.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-link"
                        disabled={selectableAvailableIds.length === 0}
                        onClick={() => handleToggleSelectVisible(visibleAvailableIds)}
                      >
                        {allSelectableSelected
                          ? (selectedFolderId ? 'Desmarcar pasta' : 'Desmarcar visíveis')
                          : (selectedFolderId ? 'Selecionar todos da pasta' : 'Selecionar visíveis')}
                      </button>
                      <span className="muted">
                        {selectedAvailableIds.size
                          ? `${selectedAvailableIds.size} selecionado(s) de ${availableCountForSelection} disponível(is)`
                          : `${availableCountForSelection} disponível(is)`}
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar caso para vincular"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ marginTop: 8 }}
                    />
                    <div className="cases-toolbar" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={selectedAvailableIds.size === 0}
                        onClick={handleLinkSelected}
                      >
                        Vincular selecionados
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={!selectedFolderId}
                        onClick={handleLinkAllFromFolder}
                      >
                        Vincular todos da pasta
                      </button>
                    </div>
                    <div className="cases-dropdown">
                      {(availableLoading) ? (
                        <div className="muted">Carregando casos...</div>
                      ) : (
                        <ul className="list compact available-cases-list">
                          {visibleAvailable.map(c => (
                              <li key={c.id} className="list-item">
                                <div className="case-row">
                                  <input
                                    type="checkbox"
                                    checked={selectedAvailableIds.has(c.id)}
                                    onChange={() => toggleAvailableSelection(c.id)}
                                  />
                                  <span>{c.title || `Caso #${c.id}`}</span>
                                </div>
                                <button type="button" className="btn btn-link" onClick={() => handleAddCase(c.id)}>
                                  Vincular
                                </button>
                              </li>
                            ))}
                          {availableFiltered.length === 0 && !availableLoading && (
                            <li className="muted">Nenhum caso disponível</li>
                          )}
                        </ul>
                      )}
                      {availableFiltered.length > visibleAvailable.length && !availableLoading && (
                        <div className="muted" style={{ marginTop: 6 }}>
                          Exibindo {visibleAvailable.length} de {availableFiltered.length}. Use "Selecionar todos da pasta" para incluir todos.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-danger" onClick={handleClose}>
                  Cancelar
                </button>
                {!readOnly && (
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar Ciclo'}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Coluna Direita: Abas Casos de Teste / Histórico */}
          <div className="edit-cycle-right">
            <div className="edit-cycle-tabs">
              <button className={tab === 'cases' ? 'edit-cycle-tab active' : 'edit-cycle-tab'} onClick={() => setTab('cases')}>
                Casos de Teste
              </button>
              <button className={tab === 'history' ? 'edit-cycle-tab active' : 'edit-cycle-tab'} onClick={() => setTab('history')}>Histórico</button>
            </div>
            <div className="edit-cycle-tab-content">
              {tab === 'cases' && (
                <div>
                  {casesLoading ? (
                    <div className="muted">Carregando casos vinculados...</div>
                  ) : (
                    <>
                      <div className="linked-cases-search">
                        <input
                          type="text"
                          placeholder="Buscar caso vinculado"
                          value={linkedSearchTerm}
                          onChange={(e) => setLinkedSearchTerm(e.target.value)}
                        />
                        <span className="muted">{linkedFiltered.length} de {linkedCases.length}</span>
                      </div>
                      <ul className="edit-cycle-list linked-cases-list">
                        {linkedFiltered.map(c => (
                          <li key={c.id} className="edit-cycle-list-item">
                            <span>{c.title || `Caso #${c.id}`}</span>
                            {!readOnly && (
                              <button type="button" className="btn btn-link danger" onClick={() => handleRemoveCase(c.id)}>
                                Remover
                              </button>
                            )}
                          </li>
                        ))}
                        {linkedCases.length === 0 && (
                          <li className="muted">Nenhum caso vinculado ao ciclo</li>
                        )}
                        {linkedCases.length > 0 && linkedFiltered.length === 0 && (
                          <li className="muted">Nenhum caso encontrado para a busca</li>
                        )}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {tab === 'history' && (
                <div>
                  {historyLoading ? (
                    <div className="muted">Carregando histórico...</div>
                  ) : (
                    <ul className="edit-cycle-list" style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {history.map(h => (
                        <li key={h.id || `${h.action_type}-${h.created_at}`} className="edit-cycle-list-item">
                          <span>
                            <strong>{getActionLabel(h.action_type)}</strong> — {h.description || 'Alteração registrada'}
                          </span>
                          <span className="muted" style={{ marginLeft: 8 }}>
                            {new Date(h.created_at || h.updated_at || Date.now()).toLocaleString()}
                          </span>
                          {h.user_name && (
                            <span className="muted" style={{ marginLeft: 8 }}>
                              por {h.user_name}
                            </span>
                          )}
                        </li>
                      ))}
                      {history.length === 0 && (
                        <li className="muted">Sem histórico para este ciclo</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default EditTestCycleModal;


